/**
 * DeviceModule — runs the Edit Mode DEVICES (`world_device` rows) on every
 * visitor's client: lights, particle emitters, signs, doors, buttons, trigger
 * volumes, teleporters, seats, moving platforms, spinners, jump pads, gravity
 * and kill zones, checkpoints, spawn points, speakers, water volumes,
 * cinematic camera paths, NPC paths, scoreboards and timers.
 *
 * THE WIRE is the world SIGNAL bus (`atlas:signal` — topic / payload / from):
 * a device emits `<name>:<event>` when something happens and obeys the topic
 * in its `listen` field; NPC scripts (`npc.signal`) and world scripts ride the
 * same bus, so a lever can open a door, start a timer, light a lamp and tell a
 * guard NPC to come look — all by name, no code (UEFN's direct event binding).
 *
 * Behaviour is EXPERIENTIAL (each client runs it — the world-script law). The
 * two things that must be authoritative go through `useDevice`: where a
 * teleporter puts you and where a checkpoint/spawn respawns you. Doors, movers,
 * spinners and seats carry static box colliders (re-seated at ~10 Hz while
 * moving) so they are solid.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { groundYIn } from '../lib/grounding.js';
import { sfx, sfxAt, sfxLater, inverseDistanceGain } from '../lib/sfx.js';
import { deviceConfig, num, str, bool, emitTopic, parseCameraKeys, sampleCameraPath, easeInOut, DEVICE_BY_KIND, type CameraKey } from '../lib/devices.js';
import { setUnderwaterFog } from './AtmosphereModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { AtlasAudioModule } from './AtlasAudioModule.js';
import { withLineNormals } from '../lib/line_geometry.js';

export interface DeviceRow {
  deviceId: bigint; worldId: bigint; owner: { toHexString(): string }; kind: string; name: string;
  posX: number; posY: number; posZ: number; yaw: number; sx: number; sy: number; sz: number;
  configJson: string; enabled: boolean; updatedAt: { microsSinceUnixEpoch: bigint };
}

interface Particle { born: number; life: number; vx: number; vy: number; vz: number }
interface Dev {
  id: string;
  row: DeviceRow;
  cfg: Record<string, unknown>;
  root: THREE.Group;
  body: THREE.Object3D | null;
  editOnly: THREE.Object3D | null; // markers shown only in Edit Mode
  label: THREE.Sprite | null;
  on: boolean;
  // kind state
  openT: number; opening: boolean; autoCloseAt: number; unlocked: boolean;
  phase: number; lastColliderAt: number; colliderOn: boolean;
  inside: boolean; fired: boolean; armed: boolean; cooldownUntil: number;
  count: number; remaining: number; running: boolean; lastTickWhole: number;
  stopAudio: (() => void) | null;
  particles: Particle[] | null; points: THREE.Points | null;
  light: THREE.PointLight | THREE.SpotLight | null;
  flickerSeed: number;
}

const INTERACT_M = 3;
const COLLIDER_HZ = 10;
const MAX_PARTICLES = 600;

function makeSprite(): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 512; canvas.height = 256;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false }));
  s.userData.canvas = canvas;
  s.renderOrder = 997;
  return s;
}

function paint(s: THREE.Sprite, lines: string[], fg: string, bg: string, heightM: number, font = 40): void {
  const canvas = s.userData.canvas as HTMLCanvasElement;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const lineH = font * 1.25;
  canvas.height = Math.max(64, Math.ceil(lineH * Math.max(1, lines.length) + 36));
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `600 ${font}px system-ui, sans-serif`;
  let w = 0;
  for (const l of lines) w = Math.max(w, ctx.measureText(l).width);
  const bw = Math.min(canvas.width, w + 48), x0 = (canvas.width - bw) / 2;
  if (bg) {
    ctx.fillStyle = bg;
    ctx.beginPath();
    const r = 18;
    ctx.moveTo(x0 + r, 0); ctx.lineTo(x0 + bw - r, 0); ctx.quadraticCurveTo(x0 + bw, 0, x0 + bw, r);
    ctx.lineTo(x0 + bw, canvas.height - r); ctx.quadraticCurveTo(x0 + bw, canvas.height, x0 + bw - r, canvas.height);
    ctx.lineTo(x0 + r, canvas.height); ctx.quadraticCurveTo(x0, canvas.height, x0, canvas.height - r);
    ctx.lineTo(x0, r); ctx.quadraticCurveTo(x0, 0, x0 + r, 0); ctx.fill();
  }
  ctx.fillStyle = fg; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
  lines.forEach((l, i) => ctx.fillText(l, canvas.width / 2, 18 + i * lineH));
  (s.material.map as THREE.CanvasTexture).needsUpdate = true;
  s.scale.set(heightM * (canvas.width / canvas.height), heightM, 1);
}

function colorOf(v: unknown, d: number): THREE.Color { const c = new THREE.Color(d); try { if (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v)) c.set(v); } catch { /* default */ } return c; }

export class DeviceModule implements IModule {
  readonly name = 'Device';
  private scene!: THREE.Scene;
  readonly root = new THREE.Group();
  private devs = new Map<string, Dev>();
  private sub: { unsubscribe(): void } | null = null;
  private subWorld: bigint | null = null;
  private seated: Dev | null = null;
  private seatRestore: { x: number; z: number } | null = null;
  private lastCheckpoint: string | null = null;
  private gravityOwner: string | null = null;
  private submergedBy: string | null = null;
  private frame = 0;
  /** The device Edit Mode holds in a gizmo (the editor owns its pose). */
  heldDeviceId: string | null = null;
  // cinematic playback
  private cine: { dev: Dev | null; name: string; keys: CameraKey[]; t0: number; dur: number; restore: { x: number; y: number; z: number; yaw: number; pitch: number; flyMode: boolean }; bars: HTMLDivElement | null; fade: HTMLDivElement | null; skippable: boolean } | null = null;
  // HUD (rules)
  private hud: HTMLDivElement | null = null;
  private score = 0;
  private roundEndsAt = 0;

  constructor(private readonly physics: PhysicsModule | null, private readonly audio: AtlasAudioModule | null) {}

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.add(this.root);
    bus.on('atlas:account-ready', () => this.bind());
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:world-cleared', () => this.teardown());
    bus.on('atlas:signal', (sig) => this.onSignal(sig.topic, sig.payload, sig.from));
    window.addEventListener('keydown', (e) => {
      const t = e.target instanceof Element ? (e.target as HTMLElement) : null; // window-dispatched events have no Element target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable || !!t.closest('.cm-editor'))) return;
      if (e.code === 'Escape' && this.cine?.skippable) { this.stopCinematic(); return; }
      if (this.seated && ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space'].includes(e.code)) { this.stand(); return; }
      if (e.code !== 'KeyE' || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      if (atlas.editMode || atlas.flyMode || atlas.spaceFlight || atlas.loadingGate) return;
      this.interact();
    });
    app.world.addSystem(makeSystem('DeviceStep', Phase.Animation, 46, (dt) => this.step(dt)));
    (window as unknown as Record<string, unknown>).__devices = {
      list: () => [...this.devs.values()].map((d) => ({ id: d.id, kind: d.row.kind, name: d.row.name, on: d.on, count: d.count })),
      trigger: (id: string) => this.trigger(String(id)),
      signal: (topic: string, payload = '') => bus.emit('atlas:signal', { topic, payload, from: 'console' }),
      cinematic: (name: string) => this.playCinematic(name),
      stopCinematic: () => this.stopCinematic(),
      state: (id: string) => this.stateByRef(String(id)),
      playing: () => (this.cine ? this.cine.name : null),
      lastCheckpoint: () => this.lastCheckpoint,
      seated: () => this.seated?.id ?? null,
      submergedBy: () => this.submergedBy,
      gravityOwner: () => this.gravityOwner,
      paths: (name: string) => this.pathPoints(String(name)),
      obstacles: () => this.obstacles().length,
    };
  }

  private bind(): void {
    const conn = atlas.conn;
    if (!conn) return;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM world_rules']); } catch { /* pre-publish */ }
    conn.db.worldDevice.onInsert((_c, row) => this.onRow(row as unknown as DeviceRow));
    conn.db.worldDevice.onUpdate((_c, _o, row) => this.onRow(row as unknown as DeviceRow));
    conn.db.worldDevice.onDelete((_c, row) => this.onRowGone(row as unknown as DeviceRow));
    conn.db.worldRules.onInsert(() => this.renderHud());
    conn.db.worldRules.onUpdate(() => this.renderHud());
    conn.db.worldRules.onDelete(() => this.renderHud());
    this.onWorld();
  }

  private onWorld(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null || wid === this.subWorld) return;
    this.teardown();
    this.subWorld = wid;
    const prev = this.sub;
    try {
      this.sub = conn.subscriptionBuilder().onApplied(() => { if (prev) prev.unsubscribe(); this.resync(); }).subscribe([`SELECT * FROM world_device WHERE world_id = ${wid}`]);
    } catch { /* pre-publish */ }
    this.score = 0; this.roundEndsAt = 0; this.lastCheckpoint = null;
    this.resync();
    this.renderHud();
  }

  private resync(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const seen = new Set<string>();
    try { for (const r of conn.db.worldDevice.iter()) if (r.worldId === wid) { seen.add(r.deviceId.toString()); this.onRow(r as unknown as DeviceRow); } } catch { /* churn */ }
    for (const [id, d] of this.devs) if (!seen.has(id)) { this.disposeDev(d); this.devs.delete(id); }
  }

  private onRow(row: DeviceRow): void {
    if (row.worldId !== atlas.currentWorldId) return;
    const id = row.deviceId.toString();
    const prev = this.devs.get(id);
    if (prev) {
      const rebuild = prev.row.kind !== row.kind || prev.row.configJson !== row.configJson || prev.row.sx !== row.sx || prev.row.sy !== row.sy || prev.row.sz !== row.sz || prev.row.enabled !== row.enabled;
      prev.row = row;
      if (rebuild) { this.disposeDev(prev); this.devs.delete(id); this.build(row); }
      else if (this.heldDeviceId !== id) { prev.root.position.set(row.posX, row.posY, row.posZ); prev.root.rotation.y = row.yaw; prev.lastColliderAt = 0; }
      return;
    }
    this.build(row);
  }

  private onRowGone(row: DeviceRow): void {
    const id = row.deviceId.toString();
    const d = this.devs.get(id);
    if (!d) return;
    this.disposeDev(d);
    this.devs.delete(id);
  }

  private teardown(): void {
    for (const d of this.devs.values()) this.disposeDev(d);
    this.devs.clear();
    this.subWorld = null;
    this.stopCinematic();
    if (this.seated) this.stand();
    if (this.gravityOwner && this.physics) { this.physics.gravityScale = 1; this.gravityOwner = null; }
    if (this.submergedBy) { atlas.submerged = false; setUnderwaterFog(null); this.submergedBy = null; }
  }

  // ─── build per kind ──────────────────────────────────────────────────────

  private build(row: DeviceRow): void {
    const cfg = deviceConfig(row.kind, row.configJson);
    const root = new THREE.Group();
    root.userData.deviceId = row.deviceId.toString();
    root.position.set(row.posX, row.posY, row.posZ);
    root.rotation.y = row.yaw;
    const d: Dev = {
      id: row.deviceId.toString(), row, cfg, root, body: null, editOnly: null, label: null,
      on: bool(cfg.onByDefault, false), openT: 0, opening: false, autoCloseAt: 0, unlocked: !bool(cfg.locked, false),
      phase: 0, lastColliderAt: 0, colliderOn: false, inside: false, fired: false, armed: true, cooldownUntil: 0,
      count: 0, remaining: num(cfg.seconds, 60), running: bool(cfg.autoStart, false), lastTickWhole: -1,
      stopAudio: null, particles: null, points: null, light: null, flickerSeed: Number(row.deviceId % 977n),
    };
    const sx = Math.max(0.05, row.sx), sy = Math.max(0.05, row.sy), sz = Math.max(0.05, row.sz);
    const box = (w: number, h: number, dd: number, color: THREE.Color, opts: { opacity?: number; emissive?: boolean; y?: number } = {}): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, dd), new THREE.MeshStandardMaterial({ color, roughness: 0.6, transparent: (opts.opacity ?? 1) < 1, opacity: opts.opacity ?? 1, emissive: opts.emissive ? color : new THREE.Color(0), emissiveIntensity: opts.emissive ? 0.6 : 0 }));
      m.position.y = opts.y ?? h / 2;
      return m;
    };
    const marker = (color: number, shape: 'box' | 'sphere' = 'box', size = 0.6): THREE.Object3D => {
      const g = shape === 'sphere' ? new THREE.SphereGeometry(size / 2, 12, 8) : new THREE.BoxGeometry(size, size, size);
      const m = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: 0.8, depthTest: false }));
      m.position.y = size / 2;
      m.renderOrder = 996;
      return m;
    };
    switch (row.kind) {
      case 'light': {
        const color = colorOf(cfg.color, 0xffd9a0);
        const intensity = num(cfg.intensity, 12), range = num(cfg.range, 14);
        if (str(cfg.type) === 'spot') {
          const l = new THREE.SpotLight(color, intensity, range, THREE.MathUtils.degToRad(num(cfg.angle, 45)), 0.4, 1.2);
          l.position.set(0, 0, 0); l.target.position.set(0, -1, 1); root.add(l.target);
          l.castShadow = bool(cfg.castShadow, false);
          d.light = l; root.add(l);
        } else {
          const l = new THREE.PointLight(color, intensity, range, 1.2);
          l.castShadow = bool(cfg.castShadow, false);
          d.light = l; root.add(l);
        }
        const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), new THREE.MeshBasicMaterial({ color }));
        d.body = bulb; root.add(bulb);
        d.editOnly = marker(0xffd27f, 'sphere', 0.8); root.add(d.editOnly);
        d.light.visible = d.on;
        break;
      }
      case 'particles': {
        const n = Math.min(MAX_PARTICLES, Math.max(8, Math.round(num(cfg.rate, 40) * num(cfg.life, 3))));
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
        const mat = new THREE.PointsMaterial({ color: colorOf(cfg.color, 0xffd27f), size: num(cfg.size, 0.12), transparent: true, opacity: 0.9, depthWrite: false, sizeAttenuation: true });
        const pts = new THREE.Points(geo, mat);
        pts.frustumCulled = false;
        d.points = pts; d.particles = Array.from({ length: n }, () => ({ born: -1, life: 0, vx: 0, vy: 0, vz: 0 }));
        root.add(pts);
        d.editOnly = marker(0xffd27f, 'sphere', 0.6); root.add(d.editOnly);
        pts.visible = d.on;
        break;
      }
      case 'sign': case 'scoreboard': case 'timer': {
        const s = makeSprite();
        d.label = s;
        s.position.y = 1.2;
        root.add(s);
        this.refreshText(d);
        d.editOnly = marker(0x9fd0ff, 'box', 0.4); root.add(d.editOnly);
        if (row.kind === 'timer' && d.running) d.remaining = num(cfg.seconds, 60);
        break;
      }
      case 'door': {
        const color = colorOf(cfg.color, 0x8a6a4a);
        const hinge = new THREE.Group();
        const motion = str(cfg.motion, 'swing');
        const panel = box(sx, sy, sz, color);
        if (motion === 'swing') { hinge.position.x = -sx / 2; panel.position.x = sx / 2; }
        hinge.add(panel);
        root.add(hinge);
        d.body = hinge;
        break;
      }
      case 'button': {
        const color = colorOf(cfg.color, 0xff8844);
        const base = box(sx, sy * 0.4, sz, new THREE.Color(0x2a3442));
        const cap = box(sx * 0.6, sy * 0.6, sz * 0.6, color, { emissive: true, y: sy * 0.4 + sy * 0.3 });
        root.add(base, cap);
        d.body = cap;
        break;
      }
      case 'trigger': case 'gravity_zone': case 'kill_zone': {
        const col = row.kind === 'trigger' ? 0x66c2ff : row.kind === 'gravity_zone' ? 0xc080ff : 0xff5060;
        const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), new THREE.MeshBasicMaterial({ color: col, wireframe: true, transparent: true, opacity: 0.5, depthTest: false }));
        m.position.y = sy / 2; m.renderOrder = 996;
        d.editOnly = m; root.add(m);
        break;
      }
      case 'teleporter': case 'checkpoint': case 'spawn_point': case 'jump_pad': {
        const col = colorOf(cfg.color, row.kind === 'teleporter' ? 0x66ffd0 : row.kind === 'checkpoint' ? 0xffd166 : row.kind === 'jump_pad' ? 0x66ff88 : 0x7ef0a0);
        const disc = new THREE.Mesh(new THREE.CylinderGeometry(Math.max(0.3, sx / 2), Math.max(0.3, sx / 2), Math.max(0.05, sy), 24), new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: 0.5, roughness: 0.4 }));
        disc.position.y = Math.max(0.05, sy) / 2;
        root.add(disc); d.body = disc;
        if (row.kind === 'spawn_point') { disc.visible = false; d.editOnly = disc; }
        const ring = new THREE.Mesh(new THREE.RingGeometry(Math.max(0.3, sx / 2) * 1.05, Math.max(0.3, sx / 2) * 1.25, 32), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.6, side: THREE.DoubleSide }));
        ring.rotation.x = -Math.PI / 2; ring.position.y = Math.max(0.05, sy) + 0.02;
        root.add(ring);
        break;
      }
      case 'seat': {
        const color = colorOf(cfg.color, 0x6a5a4a);
        const seat = box(sx, sy * 0.3, sz, color, { y: sy * 0.85 });
        const back = box(sx, sy, sz * 0.2, color, { y: sy / 2 + sy * 0.5 }); back.position.z = -sz / 2 + sz * 0.1; back.position.y = sy;
        const legs = box(sx * 0.8, sy, sz * 0.8, new THREE.Color(0x3a3030), { y: sy / 2 });
        root.add(legs, seat, back); d.body = seat;
        break;
      }
      case 'mover': case 'spinner': {
        const color = colorOf(cfg.color, row.kind === 'mover' ? 0x5a6a7a : 0x7a5a5a);
        const m = box(sx, sy, sz, color);
        root.add(m); d.body = m;
        d.phase = Number(row.deviceId % 1000n) / 1000;
        break;
      }
      case 'speaker': {
        const m = box(sx, sy, sz, new THREE.Color(0x30384a), { emissive: false });
        const grille = box(sx * 0.7, sy * 0.7, 0.02, new THREE.Color(0x66c2ff), { emissive: true, y: sy / 2 }); grille.position.z = sz / 2 + 0.011;
        root.add(m, grille); d.body = m;
        d.editOnly = marker(0x66c2ff, 'sphere', Math.min(4, num(cfg.range, 20) / 5)); root.add(d.editOnly);
        if (d.on) this.speak(d);
        break;
      }
      case 'water': {
        const color = colorOf(cfg.color, 0x2a6f9f);
        const surf = new THREE.Mesh(new THREE.PlaneGeometry(sx, sz, 1, 1), new THREE.MeshStandardMaterial({ color, transparent: true, opacity: num(cfg.opacity, 0.72), roughness: 0.15, metalness: 0.1, side: THREE.DoubleSide, depthWrite: false }));
        surf.rotation.x = -Math.PI / 2; surf.position.y = 0;
        root.add(surf); d.body = surf;
        const vol = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), new THREE.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: 0.35, depthTest: false }));
        vol.position.y = -sy / 2; vol.renderOrder = 996;
        d.editOnly = vol; root.add(vol);
        break;
      }
      case 'camera_path': case 'path': {
        const g = new THREE.Group();
        const pts = row.kind === 'camera_path' ? parseCameraKeys(cfg.keys).map((k) => new THREE.Vector3(k.x, k.y, k.z)) : (Array.isArray(cfg.points) ? (cfg.points as Array<{ x: number; z: number }>).map((p) => new THREE.Vector3(num(p.x, 0), groundYIn(atlas.conn!, atlas.currentWorldId, num(p.x, 0), num(p.z, 0)) + 0.3, num(p.z, 0))) : []);
        const col = row.kind === 'camera_path' ? 0xff9fe0 : 0x9fffb0;
        const anchor = marker(col, 'box', 0.6); g.add(anchor);
        // Keyframes/waypoints are WORLD positions — the group is un-parented from the root transform.
        const world = new THREE.Group();
        for (const p of pts) { const s = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 8), new THREE.MeshBasicMaterial({ color: col, depthTest: false })); s.position.copy(p); s.renderOrder = 996; world.add(s); }
        if (pts.length > 1) { const line = new THREE.Line(withLineNormals(new THREE.BufferGeometry().setFromPoints(pts)), new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.8, depthTest: false })); line.renderOrder = 996; world.add(line); }
        world.position.set(-row.posX, -row.posY, -row.posZ); // cancel the root translation
        world.rotation.y = -row.yaw;
        const holder = new THREE.Group(); holder.rotation.y = 0; holder.add(world);
        g.add(holder);
        d.editOnly = g; root.add(g);
        break;
      }
      default: {
        d.editOnly = marker(0xffffff, 'box', 0.5); root.add(d.editOnly);
      }
    }
    if (d.editOnly) d.editOnly.visible = atlas.editMode;
    if (!row.enabled) root.visible = atlas.editMode;
    this.root.add(root);
    this.devs.set(d.id, d);
  }

  private disposeDev(d: Dev): void {
    this.removeCollider(d);
    if (d.stopAudio) { d.stopAudio(); d.stopAudio = null; }
    if (this.seated === d) this.stand();
    if (this.gravityOwner === d.id && this.physics) { this.physics.gravityScale = 1; this.gravityOwner = null; }
    if (this.submergedBy === d.id) { atlas.submerged = false; setUnderwaterFog(null); this.submergedBy = null; }
    if (this.cine?.dev === d) this.stopCinematic();
    this.root.remove(d.root);
    d.root.traverse((o) => {
      const m = o as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
      if ((m as THREE.Mesh).geometry) (m as THREE.Mesh).geometry.dispose();
      const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
      for (const mat of mats) { const sm = mat as THREE.SpriteMaterial; sm.map?.dispose(); mat.dispose(); }
      if ((o as THREE.Light).isLight) (o as THREE.Light).dispose();
    });
  }

  // ─── colliders (doors / movers / spinners / seats) ───────────────────────

  private colliderId(d: Dev): string { return `dev:${d.id}`; }
  private removeCollider(d: Dev): void { if (d.colliderOn && this.physics) { this.physics.removeStaticCollider(this.colliderId(d)); d.colliderOn = false; } }
  private seatCollider(d: Dev, center: THREE.Vector3, yaw: number, hx: number, hy: number, hz: number): void {
    if (!this.physics) return;
    this.removeCollider(d);
    this.physics.addStaticCollider(this.colliderId(d), { shape: 'box', halfExtents: [hx, hy, hz] }, { x: center.x, y: center.y, z: center.z, yaw, scale: 1 });
    d.colliderOn = true;
  }

  // ─── signals ─────────────────────────────────────────────────────────────

  private emit(d: Dev, event: string, payload = ''): void {
    bus.emit('atlas:signal', { topic: emitTopic(d.row.name, d.cfg, event), payload, from: `device:${d.id}` });
  }

  private onSignal(topic: string, payload: string, from: string): void {
    void from;
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    // Rules score counter.
    try { const rules = conn.db.worldRules.worldId.find(wid); if (rules && rules.scoreTopic && rules.scoreTopic === topic) { const n = Number(payload); this.score += Number.isFinite(n) && payload.trim() !== '' ? n : 1; this.renderHud(); } } catch { /* churn */ }
    for (const d of this.devs.values()) {
      if (!d.row.enabled) continue;
      const listen = str(d.cfg.listen).trim();
      if (d.row.kind === 'scoreboard') {
        const t = str(d.cfg.topic, 'score').trim();
        if (t && topic === t) { const n = Number(payload); d.count += Number.isFinite(n) && payload.trim() !== '' ? n : 1; this.refreshText(d); }
        if (topic === `${d.row.name}:reset`) { d.count = 0; this.refreshText(d); }
        continue;
      }
      if (d.row.kind === 'door' && str(d.cfg.keyTopic).trim() && topic === str(d.cfg.keyTopic).trim()) { d.unlocked = true; }
      if (listen && topic === listen) this.activate(d, payload);
      else if (topic === `${d.row.name}:reset` && d.row.kind === 'timer') { d.remaining = num(d.cfg.seconds, 60); d.running = false; this.refreshText(d); }
    }
  }

  /** What a signal (or `world.device_trigger`) does to a device — the "function IN". */
  private activate(d: Dev, payload = ''): void {
    switch (d.row.kind) {
      case 'light': case 'particles': case 'mover': case 'spinner': {
        d.on = payload === 'on' ? true : payload === 'off' ? false : !d.on;
        if (d.light) d.light.visible = d.on;
        if (d.points) d.points.visible = d.on;
        break;
      }
      case 'door': this.setDoor(d, payload === 'close' ? false : payload === 'open' ? true : !d.opening, true); break;
      case 'speaker': if (d.stopAudio) { d.stopAudio(); d.stopAudio = null; } else this.speak(d); break;
      case 'timer': d.running = true; if (d.remaining <= 0) d.remaining = num(d.cfg.seconds, 60); this.refreshText(d); break;
      case 'camera_path': this.playCinematicDev(d); break;
      case 'sign': d.root.visible = !d.root.visible; break;
      case 'button': this.press(d); break;
      case 'teleporter': this.useTeleporter(d); break;
      default: break;
    }
  }

  /** Script-facing: activate a device by id or name (true when found). */
  trigger(idOrName: string): boolean {
    const d = this.find(idOrName);
    if (!d) return false;
    this.activate(d);
    return true;
  }

  find(idOrName: string): Dev | null {
    const byId = this.devs.get(idOrName);
    if (byId) return byId;
    for (const d of this.devs.values()) if (d.row.name === idOrName) return d;
    return null;
  }

  // ─── per-kind behaviour ──────────────────────────────────────────────────

  private setDoor(d: Dev, open: boolean, announce: boolean): void {
    if (open && !d.unlocked) { bus.emit('atlas:status', { text: `${d.row.name} is locked` }); return; }
    if (d.opening === open) return;
    d.opening = open;
    {
      // The servo slide comes FROM the door — a signal can open one across the map, and that one should not sound in your ear.
      const dd = Math.hypot(d.row.posX - atlas.localX, d.row.posY - atlas.localY, d.row.posZ - atlas.localZ);
      sfxAt(open ? 'sfx_door_open' : 'sfx_door_close', { x: d.row.posX, y: d.row.posY + 1, z: d.row.posZ }, { gain: 0.4 * inverseDistanceGain(dd, 6, 70), cooldownMs: 200, emitter: d.id, caption: open ? 'Door opens' : 'Door closes' });
    }
    if (announce) this.emit(d, open ? 'open' : 'close');
    if (open && num(d.cfg.autoClose, 4) > 0) d.autoCloseAt = performance.now() + num(d.cfg.autoClose, 4) * 1000;
  }

  private press(d: Dev): void {
    const now = performance.now();
    if (now < d.cooldownUntil) return;
    d.cooldownUntil = now + num(d.cfg.cooldown, 0.5) * 1000;
    sfxAt('sfx_button_press', { x: d.row.posX, y: d.row.posY + 1, z: d.row.posZ }, { gain: 0.35, cooldownMs: 150, emitter: d.id }); // the tactile clunk, at the panel
    if (str(d.cfg.mode, 'toggle') === 'toggle') { d.on = !d.on; this.emit(d, d.on ? 'on' : 'off'); }
    else this.emit(d, 'press');
    if (d.body) { const m = d.body as THREE.Mesh; (m.material as THREE.MeshStandardMaterial).emissiveIntensity = d.on || str(d.cfg.mode) !== 'toggle' ? 1.2 : 0.3; }
    bus.emit('atlas:status', { text: `${d.row.name}: ${str(d.cfg.mode) === 'toggle' ? (d.on ? 'on' : 'off') : 'pressed'}` });
  }

  private speak(d: Dev): void {
    if (!this.audio) return;
    const id = BigInt(str(d.cfg.assetId, '0') || '0');
    if (id === 0n) return;
    if (d.stopAudio) d.stopAudio();
    const p = d.root.position;
    d.stopAudio = this.audio.playClipAt(id, { x: p.x, y: p.y + 1, z: p.z }, { gain: num(d.cfg.gain, 0.8), loop: bool(d.cfg.loop, false), bus: 'sfx' });
  }

  private useTeleporter(d: Dev): void {
    const conn = atlas.conn;
    if (!conn) return;
    const tx = num(d.cfg.tx, NaN), tz = num(d.cfg.tz, NaN);
    if (!Number.isFinite(tx) || !Number.isFinite(tz)) { bus.emit('atlas:status', { text: 'This teleporter has no destination yet' }); return; }
    conn.reducers.useDevice({ deviceId: d.row.deviceId }).then(() => {
      sfx('sfx_teleport', { gain: 0.5, cooldownMs: 500 }); // the shimmer-pop
      this.physics?.teleportAvatar(tx, tz);
      atlas.localX = tx; atlas.localZ = tz;
      atlas.localY = groundYIn(conn, atlas.currentWorldId, tx, tz);
      this.emit(d, 'used');
    }).catch((e: Error) => bus.emit('atlas:status', { text: e.message }));
  }

  /** Respawn at the last checkpoint (or the world's spawn point). */
  private respawn(reason: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    sfx('sfx_player_death', { gain: 0.55, cooldownMs: 2000 });
    sfxLater(1200, () => sfx('sfx_respawn', { gain: 0.4, cooldownMs: 2000 })); // dies with the world it was scheduled in
    let target: Dev | null = this.lastCheckpoint ? this.devs.get(this.lastCheckpoint) ?? null : null;
    if (!target) for (const d of this.devs.values()) if (d.row.kind === 'spawn_point' && d.row.enabled) { target = d; break; }
    bus.emit('atlas:status', { text: reason });
    if (!target) { this.physics?.teleportAvatar(0, 0); atlas.localX = 0; atlas.localZ = 0; return; }
    const t = target;
    conn.reducers.useDevice({ deviceId: t.row.deviceId }).then(() => {
      this.physics?.teleportAvatar(t.row.posX, t.row.posZ);
      atlas.localX = t.row.posX; atlas.localZ = t.row.posZ;
      atlas.localY = groundYIn(conn, atlas.currentWorldId, t.row.posX, t.row.posZ);
    }).catch(() => { /* server said no — stay */ });
  }

  private sit(d: Dev): void {
    if (this.seated) this.stand();
    const body = atlas.localMesh;
    if (!body) return;
    this.seated = d;
    this.seatRestore = { x: atlas.localX, z: atlas.localZ };
    atlas.flyMode = true;
    const p = d.root.position;
    body.position.set(p.x, p.y + Math.max(0.05, d.row.sy) * 0.9, p.z);
    atlas.localYaw = d.row.yaw; body.rotation.y = d.row.yaw;
    this.emit(d, 'sit');
    bus.emit('atlas:status', { text: 'Sitting — move to stand up' });
  }

  private stand(): void {
    const d = this.seated;
    this.seated = null;
    atlas.flyMode = false;
    const body = atlas.localMesh;
    if (body && this.seatRestore) { body.position.set(this.seatRestore.x, groundYIn(atlas.conn!, atlas.currentWorldId, this.seatRestore.x, this.seatRestore.z), this.seatRestore.z); }
    this.seatRestore = null;
    if (d) this.emit(d, 'stand');
  }

  private interact(): void {
    let best: Dev | null = null, bd = INTERACT_M;
    for (const d of this.devs.values()) {
      if (!d.row.enabled || !DEVICE_BY_KIND.get(d.row.kind)?.interactive) continue;
      const dd = Math.hypot(d.root.position.x - atlas.localX, d.root.position.z - atlas.localZ);
      if (dd < bd) { bd = dd; best = d; }
    }
    if (!best) return;
    switch (best.row.kind) {
      case 'door': this.setDoor(best, !best.opening, true); break;
      case 'button': this.press(best); break;
      case 'teleporter': this.useTeleporter(best); break;
      case 'seat': this.sit(best); break;
      case 'speaker': this.activate(best); break;
      case 'camera_path': this.playCinematicDev(best); break;
      default: break;
    }
  }

  // ─── text boards ─────────────────────────────────────────────────────────

  private refreshText(d: Dev): void {
    if (!d.label) return;
    const cfg = d.cfg;
    const fg = str(cfg.color, '#eaf6ff'), bg = str(cfg.style) === 'plain' ? '' : str(cfg.bg, '#0a0e16');
    const scale = num(cfg.scale, 1);
    if (d.row.kind === 'sign') {
      const lines = str(cfg.text, '').split('\n').slice(0, 4).map((l) => l.slice(0, 48));
      paint(d.label, lines.length ? lines : [' '], str(cfg.style) === 'hologram' ? '#9ff2ff' : fg, str(cfg.style) === 'hologram' ? 'rgba(40,160,220,0.25)' : bg, 0.6 * scale);
      if (str(cfg.style) === 'hologram') (d.label.material as THREE.SpriteMaterial).blending = THREE.AdditiveBlending;
    } else if (d.row.kind === 'scoreboard') {
      paint(d.label, [str(cfg.title, 'Score'), String(d.count)], fg, bg, 1.0, 48);
    } else {
      const s = Math.max(0, Math.ceil(d.remaining));
      paint(d.label, [`${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`], d.running ? fg : '#9fb4cc', bg, 0.8, 56);
    }
  }

  // ─── cinematics ──────────────────────────────────────────────────────────

  playCinematic(nameOrId: string): boolean {
    const d = this.find(nameOrId);
    if (!d || d.row.kind !== 'camera_path') return false;
    this.playCinematicDev(d);
    return true;
  }

  /** Preview a keyframe list without a device (the Edit Mode ▶ Preview). */
  previewCinematic(keys: CameraKey[], duration: number, name = 'preview'): void {
    this.startCinematic(null, name, keys, duration, { letterbox: true, fade: false, skippable: true });
  }

  private playCinematicDev(d: Dev): void {
    const keys = parseCameraKeys(d.cfg.keys);
    if (keys.length < 2) { bus.emit('atlas:status', { text: `${d.row.name} needs at least two keyframes` }); return; }
    this.startCinematic(d, d.row.name, keys, num(d.cfg.duration, 8), { letterbox: bool(d.cfg.letterbox, true), fade: bool(d.cfg.fade, true), skippable: bool(d.cfg.skippable, true) });
    this.emit(d, 'start');
  }

  private startCinematic(d: Dev | null, name: string, keys: CameraKey[], duration: number, o: { letterbox: boolean; fade: boolean; skippable: boolean }): void {
    const body = atlas.localMesh;
    const cam = body?.children[0] as THREE.Object3D | undefined;
    if (!body || !cam || keys.length < 2) return;
    this.stopCinematic();
    const restore = { x: body.position.x, y: body.position.y, z: body.position.z, yaw: atlas.localYaw, pitch: cam.rotation.x, flyMode: atlas.flyMode };
    atlas.flyMode = true;
    let bars: HTMLDivElement | null = null, fade: HTMLDivElement | null = null;
    if (o.letterbox) {
      bars = document.createElement('div');
      bars.style.cssText = 'position:fixed;inset:0;z-index:70;pointer-events:none';
      for (const side of ['top:0', 'bottom:0']) { const b = document.createElement('div'); b.style.cssText = `position:absolute;left:0;right:0;${side};height:11vh;background:#000;transition:height .4s`; bars.appendChild(b); }
      const hint = document.createElement('div'); hint.textContent = o.skippable ? `${name} · Esc skips` : name; hint.style.cssText = 'position:absolute;right:18px;bottom:3vh;color:#cfe3ff;font:12px system-ui;opacity:.8';
      bars.appendChild(hint);
      document.body.appendChild(bars);
    }
    if (o.fade) {
      fade = document.createElement('div');
      fade.style.cssText = 'position:fixed;inset:0;z-index:71;pointer-events:none;background:#000;opacity:1;transition:opacity .6s';
      document.body.appendChild(fade);
      requestAnimationFrame(() => { if (fade) fade.style.opacity = '0'; });
    }
    this.cine = { dev: d, name, keys, t0: performance.now(), dur: Math.max(0.5, duration) * 1000, restore, bars, fade, skippable: o.skippable };
    bus.emit('atlas:cinematic', { playing: true, name });
  }

  stopCinematic(): void {
    const c = this.cine;
    if (!c) return;
    this.cine = null;
    const body = atlas.localMesh;
    const cam = body?.children[0] as THREE.Object3D | undefined;
    if (body && cam) {
      body.position.set(c.restore.x, c.restore.y, c.restore.z);
      atlas.localYaw = c.restore.yaw; body.rotation.y = c.restore.yaw; cam.rotation.x = c.restore.pitch;
    }
    atlas.flyMode = c.restore.flyMode; // restore, don't clobber (the admin fly cam stayed on before)
    c.bars?.remove();
    if (c.fade) { const f = c.fade; f.style.opacity = '1'; window.setTimeout(() => f.remove(), 650); }
    if (c.dev) this.emit(c.dev, 'end');
    bus.emit('atlas:cinematic', { playing: false, name: c.name });
  }

  // ─── per-frame ───────────────────────────────────────────────────────────

  private step(dt: number): void {
    this.frame++;
    const now = performance.now();
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const edit = atlas.editMode;
    const px = atlas.localX, pz = atlas.localZ, py = atlas.localY;
    const cam = atlas.localMesh?.children[0] as THREE.Object3D | undefined;
    const camY = cam ? cam.getWorldPosition(new THREE.Vector3()).y : py + 1.6;
    // Cinematic flight.
    if (this.cine) {
      const c = this.cine;
      const u = Math.min(1, (now - c.t0) / c.dur);
      const k = sampleCameraPath(c.keys, easeInOut(u));
      const body = atlas.localMesh;
      if (body && cam) { body.position.set(k.x, k.y, k.z); body.rotation.y = k.yaw; atlas.localYaw = k.yaw; cam.rotation.x = k.pitch; }
      if (u >= 1) this.stopCinematic();
    }
    // Round timer (rules).
    if (this.roundEndsAt > 0 && now >= this.roundEndsAt) { this.roundEndsAt = 0; bus.emit('atlas:signal', { topic: 'round:end', payload: '', from: 'rules' }); this.renderHud(); }
    else if (this.roundEndsAt > 0 && (this.frame & 31) === 0) this.renderHud();
    let gravityHit: Dev | null = null;
    let waterHit: Dev | null = null;
    for (const d of this.devs.values()) {
      if (d.editOnly && d.editOnly.visible !== edit) d.editOnly.visible = edit;
      if (!d.row.enabled) { if (d.root.visible !== edit) d.root.visible = edit; continue; }
      const r = d.row, cfg = d.cfg;
      const dx = px - d.root.position.x, dz = pz - d.root.position.z;
      const dist = Math.hypot(dx, dz);
      switch (r.kind) {
        case 'light': {
          const f = num(cfg.flicker, 0);
          if (d.light && d.on && f > 0) d.light.intensity = num(cfg.intensity, 12) * (1 - f * 0.5 + f * 0.5 * Math.sin(now * 0.013 + d.flickerSeed) * Math.sin(now * 0.0071 + d.flickerSeed * 3));
          break;
        }
        case 'particles': if (d.on && d.points && d.particles) this.stepParticles(d, dt, now); break;
        case 'door': {
          const target = d.opening ? 1 : 0;
          const speed = 1 / Math.max(0.2, num(cfg.speed, 1.2));
          if (d.openT !== target) {
            d.openT = target > d.openT ? Math.min(1, d.openT + dt * speed) : Math.max(0, d.openT - dt * speed);
            const motion = str(cfg.motion, 'swing');
            if (d.body) {
              if (motion === 'swing') d.body.rotation.y = -d.openT * Math.PI / 2;
              else if (motion === 'slide') d.body.position.x = -d.openT * r.sx;
              else d.body.position.y = d.openT * r.sy;
            }
            d.lastColliderAt = 0;
          }
          if (d.opening && d.autoCloseAt && now > d.autoCloseAt && dist > 2.5) { d.autoCloseAt = 0; this.setDoor(d, false, true); }
          if (now - d.lastColliderAt > 1000 / COLLIDER_HZ) { d.lastColliderAt = now; this.seatDoorCollider(d); }
          break;
        }
        case 'mover': {
          if (!d.on) break;
          const period = Math.max(1, num(cfg.period, 6)), pause = num(cfg.pause, 1), distM = num(cfg.distance, 4);
          const cycle = period + pause * 2;
          const t = ((now / 1000) + d.phase * cycle) % cycle;
          let u: number;
          if (t < period / 2) u = t / (period / 2);
          else if (t < period / 2 + pause) u = 1;
          else if (t < period + pause) u = 1 - (t - period / 2 - pause) / (period / 2);
          else u = 0;
          const eased = easeInOut(u) * distM;
          const axis = str(cfg.axis, 'y');
          const before = d.body ? d.body.position.clone() : null;
          if (d.body) { d.body.position.set(axis === 'x' ? eased : 0, (axis === 'y' ? eased : 0) + r.sy / 2, axis === 'z' ? eased : 0); }
          // Carry a rider standing on top.
          if (d.body && before) {
            const top = d.root.position.y + d.body.position.y + r.sy / 2;
            const lx = px - d.root.position.x, lz = pz - d.root.position.z;
            const c = Math.cos(-r.yaw), s = Math.sin(-r.yaw);
            const rx = lx * c - lz * s - d.body.position.x, rz = lx * s + lz * c - d.body.position.z;
            if (Math.abs(rx) <= r.sx / 2 + 0.3 && Math.abs(rz) <= r.sz / 2 + 0.3 && Math.abs(py - top) < 0.6) {
              const mdx = d.body.position.x - before.x, mdz = d.body.position.z - before.z;
              if (mdx || mdz) { const wx = mdx * Math.cos(r.yaw) + mdz * Math.sin(r.yaw), wz = -mdx * Math.sin(r.yaw) + mdz * Math.cos(r.yaw); atlas.localX += wx; atlas.localZ += wz; this.physics?.teleportAvatar(atlas.localX, atlas.localZ, true); }
            }
          }
          if (now - d.lastColliderAt > 1000 / COLLIDER_HZ) { d.lastColliderAt = now; this.seatBodyCollider(d); }
          break;
        }
        case 'spinner': {
          if (d.on && d.body) d.body.rotation.y += (num(cfg.rpm, 6) * Math.PI * 2 / 60) * dt;
          if (now - d.lastColliderAt > 1000 / COLLIDER_HZ) { d.lastColliderAt = now; this.seatBodyCollider(d); }
          break;
        }
        case 'seat': if (now - d.lastColliderAt > 2000) { d.lastColliderAt = now; this.seatBodyCollider(d); } break;
        case 'trigger': {
          const inside = this.insideBox(d, px, py + 0.9, pz);
          if (inside && !d.inside) { d.inside = true; if (!(bool(cfg.once) && d.fired)) { d.fired = true; this.emit(d, 'enter', atlas.username ?? ''); } }
          else if (!inside && d.inside) { d.inside = false; this.emit(d, 'exit', atlas.username ?? ''); }
          break;
        }
        case 'gravity_zone': if (this.insideBox(d, px, py + 0.9, pz)) gravityHit = d; break;
        case 'kill_zone': {
          const inside = this.insideBox(d, px, py + 0.5, pz);
          if (inside && !d.inside && !edit) { d.inside = true; this.emit(d, 'enter'); this.respawn(str(cfg.message, 'Ouch.')); }
          else if (!inside) d.inside = false;
          break;
        }
        case 'checkpoint': {
          const on = dist <= Math.max(0.6, r.sx / 2) + 0.4 && Math.abs(py - d.root.position.y) < 2;
          if (on && this.lastCheckpoint !== d.id) { this.lastCheckpoint = d.id; this.emit(d, 'reached'); bus.emit('atlas:status', { text: str(cfg.message, 'Checkpoint reached') }); }
          break;
        }
        case 'jump_pad': {
          const on = dist <= Math.max(0.5, r.sx / 2) + 0.3 && Math.abs(py - d.root.position.y) < 0.8;
          if (on && d.armed && !edit) { d.armed = false; this.physics?.launch(num(cfg.power, 12)); this.emit(d, 'launch'); }
          else if (!on && Math.abs(py - d.root.position.y) > 1.5) d.armed = true;
          break;
        }
        case 'water': {
          if (d.body) d.body.position.y = Math.sin(now * 0.0012 + d.flickerSeed) * num(cfg.waves, 0.15) * 0.5;
          const lx = px - d.root.position.x, lz = pz - d.root.position.z;
          const c = Math.cos(-r.yaw), s = Math.sin(-r.yaw);
          const rx = lx * c - lz * s, rz = lx * s + lz * c;
          if (Math.abs(rx) <= r.sx / 2 && Math.abs(rz) <= r.sz / 2 && camY < d.root.position.y && camY > d.root.position.y - r.sy) waterHit = d;
          break;
        }
        case 'timer': {
          if (d.running) {
            d.remaining -= dt;
            const whole = Math.ceil(d.remaining);
            if (whole !== d.lastTickWhole) { d.lastTickWhole = whole; this.refreshText(d); if (whole <= 5 && whole > 0) this.emit(d, 'tick', String(whole)); }
            if (d.remaining <= 0) { d.remaining = 0; d.running = false; this.refreshText(d); this.emit(d, 'done'); }
          }
          break;
        }
        default: break;
      }
    }
    // Gravity zones: the innermost (last) wins; leaving restores.
    if (this.physics) {
      if (gravityHit) { this.physics.gravityScale = num(gravityHit.cfg.gravity, 0.3); if (this.gravityOwner !== gravityHit.id) { this.gravityOwner = gravityHit.id; this.emit(gravityHit, 'enter'); } }
      else if (this.gravityOwner) { const d = this.devs.get(this.gravityOwner); this.physics.gravityScale = 1; this.gravityOwner = null; if (d) this.emit(d, 'exit'); }
    }
    // Water volumes own the submerged flag in worlds without an ocean module claim.
    if (waterHit && !this.submergedBy) { this.submergedBy = waterHit.id; atlas.submerged = true; setUnderwaterFog({ color: 0x06222e, density: 0.045 }); }
    else if (!waterHit && this.submergedBy) { this.submergedBy = null; atlas.submerged = false; setUnderwaterFog(null); }
    // Two owners share the flag (the ocean module writes false on ITS surface transition — a volume at sea level
    // lost its claim every other frame): re-assert while held; the other owner does the same, so it converges.
    if (this.submergedBy) atlas.submerged = true;
  }

  private insideBox(d: Dev, x: number, y: number, z: number): boolean {
    const r = d.row;
    const lx = x - d.root.position.x, lz = z - d.root.position.z;
    const c = Math.cos(-r.yaw), s = Math.sin(-r.yaw);
    const rx = lx * c - lz * s, rz = lx * s + lz * c;
    return Math.abs(rx) <= r.sx / 2 && Math.abs(rz) <= r.sz / 2 && y >= d.root.position.y && y <= d.root.position.y + r.sy;
  }

  private seatDoorCollider(d: Dev): void {
    if (!d.body) return;
    const r = d.row;
    // Open doors stop blocking (a swung panel's collider would be off-axis anyway).
    if (d.openT > 0.5) { this.removeCollider(d); return; }
    const center = new THREE.Vector3(0, r.sy / 2, 0).applyMatrix4(d.root.matrixWorld);
    this.seatCollider(d, center, r.yaw, r.sx / 2, r.sy / 2, Math.max(0.05, r.sz / 2));
  }

  private seatBodyCollider(d: Dev): void {
    if (!d.body) return;
    const r = d.row;
    d.root.updateMatrixWorld(true);
    const center = d.body.getWorldPosition(new THREE.Vector3());
    const yaw = r.yaw + (r.kind === 'spinner' ? d.body.rotation.y : 0);
    const hy = r.kind === 'seat' ? r.sy * 0.15 : r.sy / 2;
    this.seatCollider(d, center, yaw, r.sx / 2, hy, r.sz / 2);
  }

  private stepParticles(d: Dev, dt: number, now: number): void {
    const pts = d.points!, ps = d.particles!;
    const preset = str(d.cfg.preset, 'fireflies');
    const spread = num(d.cfg.spread, 1.5), speed = num(d.cfg.speed, 0.6), life = num(d.cfg.life, 3);
    const rate = num(d.cfg.rate, 40);
    const pos = pts.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = pos.array as Float32Array;
    let budget = rate * dt;
    const rnd = (): number => Math.random();
    for (let i = 0; i < ps.length; i++) {
      const p = ps[i];
      const age = p.born < 0 ? Infinity : (now - p.born) / 1000;
      if (age > p.life) {
        if (budget < 1 && p.born >= 0) { arr[i * 3 + 1] = -9999; continue; }
        if (budget < 1) { arr[i * 3 + 1] = -9999; continue; }
        budget -= 1;
        p.born = now; p.life = life * (0.6 + rnd() * 0.8);
        const a = rnd() * Math.PI * 2, rr = rnd() * spread;
        let x = Math.cos(a) * rr, y = 0, z = Math.sin(a) * rr;
        switch (preset) {
          case 'rain': case 'snow': y = spread + rnd() * 2; p.vx = 0; p.vy = preset === 'rain' ? -8 - speed * 4 : -0.6 - speed; p.vz = 0; break;
          case 'fountain': x *= 0.1; z *= 0.1; p.vx = (rnd() - 0.5) * speed; p.vy = 4 + speed * 2; p.vz = (rnd() - 0.5) * speed; break;
          case 'sparks': x *= 0.2; z *= 0.2; p.vx = (rnd() - 0.5) * 6 * speed; p.vy = 2 + rnd() * 4 * speed; p.vz = (rnd() - 0.5) * 6 * speed; break;
          case 'smoke': x *= 0.4; z *= 0.4; p.vx = (rnd() - 0.5) * 0.3; p.vy = 0.8 + speed; p.vz = (rnd() - 0.5) * 0.3; break;
          case 'embers': x *= 0.5; z *= 0.5; p.vx = (rnd() - 0.5) * 0.8; p.vy = 0.6 + speed; p.vz = (rnd() - 0.5) * 0.8; break;
          case 'dust': y = rnd() * spread; p.vx = (rnd() - 0.5) * 0.2 * speed; p.vy = (rnd() - 0.5) * 0.1; p.vz = (rnd() - 0.5) * 0.2 * speed; break;
          default: y = rnd() * spread; p.vx = (rnd() - 0.5) * speed; p.vy = (rnd() - 0.5) * speed * 0.5; p.vz = (rnd() - 0.5) * speed; break; // fireflies
        }
        arr[i * 3] = x; arr[i * 3 + 1] = y; arr[i * 3 + 2] = z;
        continue;
      }
      const grav = preset === 'sparks' || preset === 'fountain' ? 9.81 : 0;
      p.vy -= grav * dt;
      arr[i * 3] += p.vx * dt; arr[i * 3 + 1] += p.vy * dt; arr[i * 3 + 2] += p.vz * dt;
      if (preset === 'fireflies') { p.vx += (Math.random() - 0.5) * 2 * dt; p.vz += (Math.random() - 0.5) * 2 * dt; }
      if ((preset === 'rain' || preset === 'snow' || preset === 'fountain' || preset === 'sparks') && arr[i * 3 + 1] < 0) p.born = -1;
    }
    pos.needsUpdate = true;
  }

  // ─── rules HUD ───────────────────────────────────────────────────────────

  private renderHud(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    let rules: { objective: string; roundSeconds: number; scoreTopic: string } | undefined;
    try { rules = conn && wid !== null ? (conn.db.worldRules.worldId.find(wid) ?? undefined) : undefined; } catch { rules = undefined; }
    const lines: string[] = [];
    if (rules?.objective) lines.push(`◆ ${rules.objective}`);
    if (rules && rules.roundSeconds > 0) {
      if (this.roundEndsAt === 0) this.roundEndsAt = performance.now() + rules.roundSeconds * 1000;
      const s = Math.max(0, Math.ceil((this.roundEndsAt - performance.now()) / 1000));
      lines.push(`⏱ ${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`);
    }
    if (rules?.scoreTopic) lines.push(`★ ${this.score}`);
    if (lines.length === 0) { this.hud?.remove(); this.hud = null; return; }
    if (!this.hud) {
      this.hud = document.createElement('div');
      this.hud.id = 'rules-hud';
      this.hud.style.cssText = 'position:fixed;top:100px;left:12px;z-index:40;pointer-events:none;display:flex;flex-direction:column;gap:3px;font:600 13px/1.4 system-ui;color:#ffe9a8;text-shadow:0 1px 3px rgba(0,0,0,.9)';
      document.body.appendChild(this.hud);
    }
    this.hud.textContent = '';
    for (const l of lines) { const d = document.createElement('div'); d.textContent = l; this.hud.appendChild(d); }
  }

  // ─── queries for scripts + Edit Mode ─────────────────────────────────────

  ids(): string[] { return [...this.devs.keys()].sort((a, b) => (BigInt(a) < BigInt(b) ? -1 : 1)); }
  rowByRef(idOrName: string): DeviceRow | null { return this.find(idOrName)?.row ?? null; }
  posByRef(idOrName: string): { x: number; y: number; z: number } | null { const d = this.find(idOrName); return d ? { x: d.root.position.x, y: d.root.position.y, z: d.root.position.z } : null; }
  stateByRef(idOrName: string): string { const d = this.find(idOrName); return d ? this.stateOf(d.id) : ''; }
  rowOf(id: string): DeviceRow | null { return this.devs.get(id)?.row ?? null; }
  deviceMesh(id: string): THREE.Object3D | null { return this.devs.get(id)?.root ?? null; }
  stateOf(id: string): string {
    const d = this.devs.get(id);
    if (!d) return '';
    switch (d.row.kind) {
      case 'door': return d.opening ? 'open' : 'closed';
      case 'timer': return d.running ? `running:${Math.ceil(d.remaining)}` : `stopped:${Math.ceil(d.remaining)}`;
      case 'scoreboard': return String(d.count);
      default: return d.on ? 'on' : 'off';
    }
  }
  /** Waypoints of a `path` device by name/id (ground-level XZ). */
  pathPoints(nameOrId: string): Array<{ x: number; z: number }> {
    const d = this.find(nameOrId);
    if (!d || d.row.kind !== 'path') return [];
    const raw = Array.isArray(d.cfg.points) ? d.cfg.points as Array<{ x: unknown; z: unknown }> : [];
    return raw.map((p) => ({ x: num(p.x, 0), z: num(p.z, 0) }));
  }
  pathLoops(nameOrId: string): boolean { const d = this.find(nameOrId); return !!d && bool(d.cfg.loop, true); }
  /** Solid device footprints for NPC pathfinding (closed doors, platforms, seats, signs on posts are skipped). */
  obstacles(): Array<{ x: number; z: number; hx: number; hz: number }> {
    const out: Array<{ x: number; z: number; hx: number; hz: number }> = [];
    for (const d of this.devs.values()) {
      if (!d.row.enabled) continue;
      if (d.row.kind === 'door' && d.opening) continue;
      if (!['door', 'mover', 'spinner', 'seat', 'speaker'].includes(d.row.kind)) continue;
      const r = Math.max(d.row.sx, d.row.sz) / 2;
      out.push({ x: d.root.position.x, z: d.root.position.z, hx: r, hz: r });
    }
    return out;
  }

  dispose(): void {
    this.teardown();
    this.scene.remove(this.root);
    this.hud?.remove();
  }
}
